import { ActionIcon, Switch, TextInput, Tooltip } from '@mantine/core'
import { X } from 'lucide-react'
import type { DriverIQUdfSlot } from 'types/graphql'

import IconInfoCircle from 'src/icons/IconInfoCircle'
import { cn } from 'src/utils'

import {
  isSlotActive,
  UDF_SLOTS,
  type DriverIQUdfFieldsEditor,
} from './useDriverIQUdfFieldsEditor'

interface DriverIQUdfFieldsSectionProps {
  editor: DriverIQUdfFieldsEditor
  editing: boolean
}

const DriverIQUdfFieldsSection = ({
  editor,
  editing,
}: DriverIQUdfFieldsSectionProps) => {
  const {
    rows,
    selectedSlot,
    selectSlot,
    setFieldName,
    setRequired,
    setOption,
    addOption,
    removeOption,
    errors,
  } = editor

  const renderSummary = () => (
    <div
      data-testid="udf-summary"
      className="flex flex-wrap gap-x-7 gap-y-1.5 text-xs text-doubleNickel-gray-600"
    >
      {UDF_SLOTS.map((slot) => {
        const row = rows[slot]
        const active = isSlotActive(row)
        return (
          <span key={slot} data-testid={`udf-summary-${slot}`}>
            <span className="mr-1 font-mono text-[11px] text-doubleNickel-gray-500">
              {slot}
            </span>{' '}
            {active ? (
              <span className="font-medium text-doubleNickel-gray-700">
                {row.fieldName.trim()}
              </span>
            ) : (
              <span className="text-doubleNickel-gray-400">Not set</span>
            )}
          </span>
        )
      })}
    </div>
  )

  const renderSlotList = () => (
    <div className="flex flex-col border-r border-doubleNickel-gray-200 bg-doubleNickel-gray-50">
      {UDF_SLOTS.map((slot) => {
        const row = rows[slot]
        const active = isSlotActive(row)
        const selected = slot === selectedSlot
        return (
          <button
            key={slot}
            type="button"
            data-testid={`udf-slot-${slot}`}
            aria-pressed={selected}
            onClick={() => selectSlot(slot)}
            className={cn(
              'flex flex-col gap-0.5 border-b border-doubleNickel-gray-200 px-3 py-2.5 text-left text-[13px]',
              selected
                ? 'bg-white font-medium text-doubleNickel-gray-900 shadow-[inset_2px_0_0_theme(colors.doubleNickel.brand.500)]'
                : 'text-doubleNickel-gray-700 hover:bg-doubleNickel-gray-100',
              !active && 'text-doubleNickel-gray-400'
            )}
          >
            <span
              className={cn(
                'font-mono text-[11px] font-normal',
                active
                  ? 'text-doubleNickel-gray-500'
                  : 'text-doubleNickel-gray-400'
              )}
            >
              {slot}
            </span>
            <span className="truncate">
              {active ? row.fieldName.trim() : 'Not set'}
            </span>
            {errors[slot] && !selected && (
              <span className="text-[11px] font-normal text-doubleNickel-error-500">
                Needs attention
              </span>
            )}
          </button>
        )
      })}
    </div>
  )

  const renderSlotPane = (slot: DriverIQUdfSlot) => {
    const row = rows[slot]
    const active = isSlotActive(row)
    const error = errors[slot]

    return (
      <div className="flex flex-col gap-2.5 p-3">
        <div className="grid grid-cols-[1fr_auto] items-end gap-3">
          <TextInput
            size="xs"
            label="Field name"
            placeholder="e.g. Cost center"
            value={row.fieldName}
            maxLength={100}
            onChange={(e) => setFieldName(slot, e.target.value)}
            data-testid="udf-field-name"
            classNames={{
              label: 'text-xs font-medium text-doubleNickel-gray-700 mb-1',
            }}
          />
          <div className="flex h-[30px] items-center">
            <Switch
              size="xs"
              label="Required"
              checked={row.isRequired}
              disabled={!active}
              onChange={(e) => setRequired(slot, e.currentTarget.checked)}
              data-testid="udf-required"
              classNames={{
                label: 'text-[13px] text-doubleNickel-gray-700 pl-1.5',
              }}
            />
          </div>
        </div>
        {!active && (
          <div className="text-xs text-doubleNickel-gray-500">
            Enter a field name to activate this slot.
          </div>
        )}

        <div className="grid grid-cols-[1fr_1fr_24px] gap-2 text-xs font-medium text-doubleNickel-gray-600">
          <span>DriverIQ code *</span>
          <span>Shown to recruiters as</span>
          <span />
        </div>
        {row.options.map((option, index) => (
          <div
            key={index}
            className="grid grid-cols-[1fr_1fr_24px] items-center gap-2"
          >
            <TextInput
              size="xs"
              value={option.code}
              maxLength={100}
              onChange={(e) => setOption(slot, index, { code: e.target.value })}
              data-testid={`udf-code-${index}`}
              aria-label={`${slot} option ${index + 1} DriverIQ code`}
              classNames={{ input: 'font-mono text-xs' }}
            />
            <TextInput
              size="xs"
              value={option.name}
              maxLength={100}
              placeholder={option.code.trim() || 'Same as code'}
              onChange={(e) => setOption(slot, index, { name: e.target.value })}
              data-testid={`udf-name-${index}`}
              aria-label={`${slot} option ${
                index + 1
              } name shown to recruiters`}
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => removeOption(slot, index)}
              disabled={row.options.length === 1}
              title={
                row.options.length === 1
                  ? 'At least one option is required'
                  : 'Remove option'
              }
              aria-label={`Remove ${slot} option ${index + 1}`}
              data-testid={`udf-remove-option-${index}`}
            >
              <X size={14} />
            </ActionIcon>
          </div>
        ))}
        {error && (
          <div
            data-testid="udf-error"
            className="text-xs text-doubleNickel-error-500"
          >
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={() => addOption(slot)}
          data-testid="udf-add-option"
          className="self-start text-xs font-medium text-doubleNickel-brand-600 hover:underline"
        >
          + Add option
        </button>
        <div className="text-xs text-doubleNickel-gray-500">
          Code is sent on the order and billed by Cisive. The name is optional;
          blank shows the code.
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="udf-section"
      className="-mt-1 border-t border-dashed border-doubleNickel-gray-200 pt-2"
    >
      <div className="mb-1.5 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-doubleNickel-gray-500">
        Order fields
        <Tooltip
          label="Generic DriverIQ order fields (UDF1–UDF3). Recruiters pick from your options when requesting a DriverIQ package."
          multiline
          maw={300}
          withArrow
        >
          <div className="flex items-center">
            <IconInfoCircle className="h-3 w-3 fill-none stroke-doubleNickel-gray-500" />
          </div>
        </Tooltip>
      </div>

      {editing ? (
        <div className="grid grid-cols-[160px_1fr] overflow-hidden rounded-lg border border-doubleNickel-gray-200">
          {renderSlotList()}
          {renderSlotPane(selectedSlot)}
        </div>
      ) : (
        renderSummary()
      )}
    </div>
  )
}

export default DriverIQUdfFieldsSection
